import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  NativeSelect,
  Text,
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { LuChevronLeft, LuEllipsis, LuPause, LuPlay } from 'react-icons/lu'
import { LuLogOut } from 'react-icons/lu'
import { DisplayControlsToggle } from '../DisplayControlsToggle'
import { LanguageSwitcher } from '../LanguageSwitcher'
import { ColorModeButton } from '../ui/color-mode'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from '../../i18n'
import { PresenceChips } from './PresenceChips'

// One row, about 34px, and then the grid gets everything else.
//
// Everything a moderator touches once a session — grid size, display controls,
// bulk actions, the shortcut list — lives behind the ⋯ button, so the row holds
// only what changes during a shift: run/pause, which videos, and who else is
// working.

const FILTERS = ['pending', 'all', 'accepted', 'rejected']

function OverflowPanel({
  grid,
  onGridChange,
  canControl,
  displayControlsEnabled,
  onDisplayControlsChange,
  bulk,
  onLogout,
  onClose,
}) {
  const { t } = useTranslation()
  const ref = useRef(null)

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    const onKey = (e) => e.key === 'Escape' && onClose()
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return (
    <Box
      ref={ref}
      position="absolute"
      top="100%"
      right="6px"
      mt="4px"
      zIndex={30}
      minW="260px"
      p={3}
      borderRadius="md"
      bg="var(--mc-bg-surface)"
      border="1px solid var(--mc-border)"
      backdropFilter="blur(8px)"
      boxShadow="var(--mc-shadow-pop)"
    >
      <Flex direction="column" gap={3}>
        <HStack gap={2}>
          <Text fontSize="xs" color="var(--mc-text-muted)" minW="52px">
            {t('moderation.grid')}
          </Text>
          <NativeSelect.Root size="xs" width="64px">
            <NativeSelect.Field
              data-testid="grid-rows"
              value={grid.rows}
              onChange={(e) => onGridChange({ ...grid, rows: parseInt(e.target.value) })}
            >
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <Text fontSize="xs" color="var(--mc-text-muted)">×</Text>
          <NativeSelect.Root size="xs" width="64px">
            <NativeSelect.Field
              data-testid="grid-cols"
              value={grid.cols}
              onChange={(e) => onGridChange({ ...grid, cols: parseInt(e.target.value) })}
            >
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </HStack>

        <HStack gap={2}>
          <Button
            size="xs"
            colorPalette="green"
            variant="outline"
            flex="1"
            loading={bulk.loading}
            disabled={bulk.count === 0}
            onClick={() => bulk.onBulk(true)}
          >
            {t('moderation.acceptWatched', { count: bulk.count })}
          </Button>
          <Button
            size="xs"
            colorPalette="red"
            variant="outline"
            flex="1"
            loading={bulk.loading}
            disabled={bulk.count === 0}
            onClick={() => bulk.onBulk(false)}
          >
            {t('moderation.rejectWatched', { count: bulk.count })}
          </Button>
        </HStack>

        {canControl && (
          <DisplayControlsToggle
            size="xs"
            enabled={displayControlsEnabled}
            onChange={onDisplayControlsChange}
          />
        )}

        <Text fontSize="0.65rem" color="var(--mc-text-muted)" lineHeight="1.5">
          {t('moderation.keyboardHints')}
        </Text>

        {/* This screen runs outside the app header, so the handful of things
            that header carried have to live somewhere. */}
        <HStack gap={2} pt={1} borderTop="1px solid var(--mc-border)">
          <LanguageSwitcher />
          <ColorModeButton />
          <Box flex="1" />
          <Button size="xs" variant="ghost" color="var(--mc-steel)" onClick={onLogout}>
            <HStack gap={1.5}>
              <LuLogOut />
              <Text fontSize="xs">{t('common.logout')}</Text>
            </HStack>
          </Button>
        </HStack>
      </Flex>
    </Box>
  )
}

export function ModerationToolbar({
  onBack,
  paused,
  onTogglePaused,
  queueCount,
  filterMode,
  onFilterMode,
  challenges,
  selectedChallenges,
  onToggleChallenge,
  moderators,
  serverNow,
  grid,
  onGridChange,
  canControl,
  displayControlsEnabled,
  onDisplayControlsChange,
  bulk,
}) {
  const { t } = useTranslation()
  const { logout } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <Flex
      position="relative"
      align="center"
      gap={2}
      px={2}
      py={1}
      flexShrink={0}
      borderBottom="1px solid var(--mc-border)"
      bg="var(--mc-bg-header)"
    >
      <IconButton
        aria-label={t('common.back')}
        size="xs"
        variant="ghost"
        color="var(--mc-steel)"
        onClick={onBack}
        flexShrink={0}
      >
        <LuChevronLeft />
      </IconButton>

      {/* The one control that has to be readable from across a room. */}
      <Button
        size="sm"
        flexShrink={0}
        px={3}
        colorPalette={paused ? 'green' : 'yellow'}
        variant="solid"
        data-testid="mod-toggle"
        data-paused={paused ? 'true' : 'false'}
        onClick={onTogglePaused}
      >
        <HStack gap={1.5}>
          {paused ? <LuPlay /> : <LuPause />}
          <Text fontSize="xs" fontWeight="700" letterSpacing="0.05em">
            {paused ? t('moderation.start') : t('moderation.pause')}
          </Text>
        </HStack>
      </Button>

      <Text
        fontSize="xs"
        color={queueCount > 0 ? 'var(--mc-amber)' : 'var(--mc-text-muted)'}
        fontFamily="'JetBrains Mono', monospace"
        whiteSpace="nowrap"
        flexShrink={0}
      >
        {t('moderation.inQueue', { count: queueCount })}
      </Text>

      <Flex
        align="center"
        gap={2}
        minW={0}
        flex="1"
        overflowX="auto"
        css={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
      >
      <HStack gap={0} flexShrink={0} borderRadius="md" overflow="hidden" border="1px solid var(--mc-border)">
        {FILTERS.map((key) => (
          <Box
            key={key}
            as="button"
            data-filter={key}
            px={2}
            py={1}
            fontSize="xs"
            lineHeight="1"
            cursor="pointer"
            bg={filterMode === key ? 'var(--mc-bg-selected)' : 'transparent'}
            color={filterMode === key ? 'var(--mc-text)' : 'var(--mc-steel)'}
            _hover={{ bg: filterMode === key ? 'var(--mc-bg-selected)' : 'var(--mc-bg-raised)' }}
            onClick={() => onFilterMode(key)}
          >
            {t(`moderation.filter${key[0].toUpperCase()}${key.slice(1)}`)}
          </Box>
        ))}
      </HStack>

      {challenges.length > 0 && (
        <HStack gap={1} flexShrink={0}>
          {challenges.map((c) => (
            <Badge
              key={c.id}
              data-challenge-pill={c.id}
              cursor="pointer"
              bg={selectedChallenges.includes(c.id) ? c.color || 'var(--mc-text)' : 'var(--mc-fill-soft)'}
              // The yellow fallback needs dark text; a challenge's own colour
              // keeps the white it always had.
              color={selectedChallenges.includes(c.id) ? c.textColor || (c.color ? 'white' : 'var(--mc-bg)') : 'var(--mc-steel)'}
              fontSize="0.65rem"
              px={2}
              py={0.5}
              borderRadius="full"
              whiteSpace="nowrap"
              onClick={() => onToggleChallenge(c.id)}
              opacity={selectedChallenges.length === 0 || selectedChallenges.includes(c.id) ? 1 : 0.4}
            >
              {c.label || c.id}
            </Badge>
          ))}
        </HStack>
      )}

      </Flex>

      <PresenceChips moderators={moderators} serverNow={serverNow} />

      <IconButton
        aria-label={t('moderation.moreOptions')}
        data-testid="mod-overflow"
        size="xs"
        variant="ghost"
        color="var(--mc-steel)"
        flexShrink={0}
        onClick={() => setMenuOpen((v) => !v)}
      >
        <LuEllipsis />
      </IconButton>

      {menuOpen && (
        <OverflowPanel
          grid={grid}
          onGridChange={onGridChange}
          canControl={canControl}
          displayControlsEnabled={displayControlsEnabled}
          onDisplayControlsChange={onDisplayControlsChange}
          bulk={bulk}
          onLogout={logout}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </Flex>
  )
}
